<script setup>
import { inject, ref, computed, watch, nextTick, provide } from 'vue';
import { TypeNommage } from '@/scripts/global.js';
import { 
    getErreurNomNorme,
    getErreurTexteRequis, 
    getErreurValeurListeRequise } from '@/scripts/controlesSaisie.js';
import { 
    apiModifierArgumentsRoutine } from '@/services/ServiceModele.js';
import Dialog from 'primevue/dialog';
import SaisieTexte from '@/components/generiques/SaisieTexte.vue';
import SaisieTypeDonnee from '@/components/generiques/SaisieTypeDonnee.vue';

const { routine } = defineProps({
    routine: Object
});

const visible = defineModel('visible', {
    type: Boolean
});

const getTypeDonnee = inject('getTypeDonnee');
const appelerApiModifModele = inject('appelerApiModifModele');
const afficherSucces = inject('afficherSucces');
const ouvrirFenetreFormule = inject('ouvrirFenetreFormule');

const argumentsRoutine = ref(getArguments(routine));
const verifErreursSaisie = ref(false);

const erreursSaisie = computed(() => {
    const erreurs = {};
    if (verifErreursSaisie.value) {
        for (let [indiceArg, argument] of argumentsRoutine.value.entries()) {
            erreurs[`arg_${indiceArg}_nom`] = getErreurNomNorme(argument.nom, TypeNommage.VARIABLE);
            erreurs[`arg_${indiceArg}_libelle`] = getErreurTexteRequis(argument.libelle);
            erreurs[`arg_${indiceArg}_type`] = getErreurValeurListeRequise(argument.typeDonnee);
        }
    }
    return erreurs;
});

const saisieValide = computed(() => {
    return !Object.keys(erreursSaisie.value).map(cle => erreursSaisie.value[cle]).some(erreur => erreur !== null);
});

watch(() => visible.value, (visible) => {
    if (!visible) {
        verifErreursSaisie.value = false;
    }
});

watch(() => routine, (r) => {
    argumentsRoutine.value = getArguments(r);
});

function getArguments(routine) {
    return routine.arguments.map(arg => {
        return {nom: arg.nom, libelle: arg.libelle, typeDonnee : getTypeDonnee(arg.typeDonnee)};
    });
}

function incrementerPositionArgument(argument, increment) {
    const indiceArgument = argumentsRoutine.value.indexOf(argument);
    const nouvelIndice = indiceArgument + increment;
    if (nouvelIndice >= 0 && nouvelIndice < argumentsRoutine.value.length) {
        argumentsRoutine.value.splice(indiceArgument, 1);
        argumentsRoutine.value.splice(nouvelIndice, 0, argument);
    }
}

function ajouterArgument() {
    argumentsRoutine.value.push({
        nom: null,
        libelle: null,
        typeDonnee: null
    });
}

function supprimerArgument(argument) {
    const indiceArgument = argumentsRoutine.value.indexOf(argument);
    argumentsRoutine.value.splice(indiceArgument, 1);
}

function valider() {
    verifErreursSaisie.value = true;
    nextTick(() => {
        if (saisieValide.value) {
            appelerApiModifModele(apiModifierArgumentsRoutine, routine.id, argumentsRoutine.value, (reponseOK) => {
                if (reponseOK) {
                    visible.value = false;
                    afficherSucces('Arguments enregistrés');
                    if (!routine.formule.texte) {
                        ouvrirFenetreFormule(routine, true);
                    }
                }
            });
        }
    });
}

provide('erreursSaisie', erreursSaisie);

</script>

<template>
    <Dialog 
    v-model:visible="visible"
    :modal="true">
        <template #header>
            <label class="titre">Edition des arguments d'une routine</label>
        </template>
        <div class="corps-modale" v-if="routine">
            <div class="liste-arguments">
                <label class="intitule"></label>
                <label class="intitule">Nom</label>
                <label class="intitule">Libellé</label>
                <label class="intitule">Type de donnée</label>
                <label class="intitule"></label>
                <template v-if="argumentsRoutine.length === 0">
                    <label />
                    <label class="aucun-argument">Aucun argument</label>
                </template>
                <template v-for="(argument, indiceArg) in argumentsRoutine">
                    <div class="boutons-deplacement">
                        <button class="bouton fas fa-arrow-up" 
                          v-tooltip="'Déplacer avant'"
                          @click="incrementerPositionArgument(argument, -1)"/>
                        <button class="bouton fas fa-arrow-down" 
                          v-tooltip="'Déplacer après'"
                          @click="incrementerPositionArgument(argument, 1)"/>
                    </div>
                    <SaisieTexte 
                        v-model:valeur="argument.nom" 
                        :id-erreur="`arg_${indiceArg}_nom`" />
                    <SaisieTexte 
                        v-model:valeur="argument.libelle" 
                        :id-erreur="`arg_${indiceArg}_libelle`" />
                    <SaisieTypeDonnee 
                        v-model:valeur="argument.typeDonnee" 
                        :id-erreur="`arg_${indiceArg}_type`"
                        :proposer-types-objets="false" />
                    <button class="bouton fas fa-trash btn-supprimer" 
                      v-tooltip="'Supprimer'"
                      @click="supprimerArgument(argument)"/>
                </template>
            </div>
            <button class="bouton fas fa-plus" 
              v-tooltip="'Ajouter un argument'"
              @click="ajouterArgument()"/>
        </div>
        <template #footer>
            <button 
                class="bouton btn-primaire fas fa-check" 
                @click="valider()"/>
        </template>
    </Dialog>
</template>

<style scoped>

.titre {
    margin-right: 10px;
}

.corps-modale {
    display: flex;
	flex-direction: column;
    gap: 20px;
}

.aucun-argument {
    font-style: italic;
}

.liste-arguments {
    display: grid;
	grid-template-columns: auto auto auto auto auto;
	gap: 2px 10px;
	align-items: stretch;
    justify-items: stretch;
    margin-top: 2px;
    min-width: 25vw;
}

.boutons-deplacement {
    display: flex;
    flex-direction: row;
    gap: 5px;
}

</style>
